/**
 * 分析アプリの AI 解析の観点 (docs/29 §8-1)。
 *
 * 地区が複数の観点 (例: 防災の視点 / Enabling City の視点) で解析しているとき、マトリクス・分析・地図の
 * 絞り込み・ヒートマップ・ネットワーク図の軸の候補に、観点ごとの AI 解析の軸を並べる。
 *
 * - 既定の観点の軸の key は従来どおり (例 `emotion.major`)。M5 表示設定に保存された key はそのまま使える。
 * - 追加の観点の軸の key は `analysis:<観点 key>|<元の key>` (例 `analysis:enabling-city|emotion.major`)。
 * - 軸の値は、その観点の結果に写した投稿 (`projectPostAnalysis`) と、その観点の出力フォーマット・
 *   カテゴリマスタで計算する。投稿者入力項目の軸 (`postField:`) は観点に関係ないので付けない。
 * - AI 認識物単位の行 (擬似投稿、id `<投稿 ID>__cls…`) は元の投稿の観点の結果で値を出す。
 */
import { useEffect, useMemo, useState } from 'react';
import {
  applyOutputFieldsOverride,
  type AiOutputFormat,
  type PostAnalysisOption,
} from '@citizen-walk/shared-types';
import {
  buildCategoryMastersResult,
  loadCategoryMastersForSettings,
  projectPostAnalysis,
  type AiCategoryMastersResult,
  type PostMinimal,
} from '../posts';
import { loadAiOutputFormat } from './aiFormatLoader';

/** 追加の観点の軸の key の接頭辞。 */
export const PERSPECTIVE_AXIS_PREFIX = 'analysis:';
const SEP = '|';

/** 追加の観点 1 つ分の分析の材料。 */
export interface AnalysisPerspective {
  key: string;
  label: string;
  resolver: AiCategoryMastersResult;
  outputFormat: AiOutputFormat | null;
  /** 元の投稿 (id → 投稿)。AI 認識物単位の行から元の投稿をたどるのに使う。 */
  postsById: Map<string, PostMinimal>;
}

/** 追加の観点の軸の key を作る。 */
export function perspectiveAxisKey(perspectiveKey: string, baseKey: string): string {
  return `${PERSPECTIVE_AXIS_PREFIX}${perspectiveKey}${SEP}${baseKey}`;
}

/** 軸の key を分解する。接頭辞が無ければ既定の観点 (perspectiveKey = null)。 */
export function parsePerspectiveAxisKey(key: string): {
  perspectiveKey: string | null;
  baseKey: string;
} {
  if (!key.startsWith(PERSPECTIVE_AXIS_PREFIX)) return { perspectiveKey: null, baseKey: key };
  const rest = key.slice(PERSPECTIVE_AXIS_PREFIX.length);
  const i = rest.indexOf(SEP);
  if (i <= 0) return { perspectiveKey: null, baseKey: key };
  return { perspectiveKey: rest.slice(0, i), baseKey: rest.slice(i + 1) };
}

/** 軸の key が指す観点 (追加の観点のときだけ) と元の key。 */
export function resolvePerspectiveAxis(
  key: string,
  perspectives: readonly AnalysisPerspective[] | undefined,
): { perspective: AnalysisPerspective | null; baseKey: string } {
  const { perspectiveKey, baseKey } = parsePerspectiveAxisKey(key);
  if (!perspectiveKey) return { perspective: null, baseKey: key };
  const perspective = perspectives?.find((p) => p.key === perspectiveKey) ?? null;
  return { perspective, baseKey };
}

/** 投稿を観点の結果に写す (AI 認識物単位の行は元の投稿で写す)。 */
export function projectForPerspective(
  post: PostMinimal,
  perspective: AnalysisPerspective,
): PostMinimal {
  const parentId = post.id.includes('__cls') ? post.id.slice(0, post.id.indexOf('__cls')) : post.id;
  const source = perspective.postsById.get(parentId) ?? post;
  return projectPostAnalysis(source, perspective.key);
}

/** 観点の軸のラベル (例「【Enabling City の視点】感情（大分類）」)。 */
export function perspectiveAxisLabel(perspectiveLabel: string, baseLabel: string): string {
  return `【${perspectiveLabel}】${baseLabel}`;
}

interface LoadedPerspective {
  key: string;
  resolver: AiCategoryMastersResult;
  outputFormat: AiOutputFormat | null;
}

/**
 * 地区の追加の観点の分析の材料を読み込む。追加の観点が無い地区は空配列 (従来どおり)。
 * 既定の観点は従来の resolver / outputFormat を使うのでここには含めない。
 */
export function useAnalysisPerspectives(
  options: readonly PostAnalysisOption[] | null | undefined,
  posts: readonly PostMinimal[],
): AnalysisPerspective[] {
  const additional = useMemo(() => (options ?? []).filter((o) => !o.isDefault), [options]);
  const loadKey = additional
    .map((o) => `${o.key}:${o.settingsId}:${o.outputFormatIdOverride ?? ''}`)
    .join(',');
  const [loaded, setLoaded] = useState<LoadedPerspective[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (additional.length === 0) return;
    void Promise.all(
      additional.map(async (o): Promise<LoadedPerspective> => {
        if (!o.settingsId) {
          return { key: o.key, resolver: buildCategoryMastersResult([]), outputFormat: null };
        }
        const [masters, fmt] = await Promise.all([
          loadCategoryMastersForSettings(o.settingsId),
          loadAiOutputFormat(o.settingsId, o.outputFormatIdOverride ?? null),
        ]);
        const schema = applyOutputFieldsOverride(
          fmt.format?.schema ?? null,
          o.outputFieldsOverride ?? null,
        );
        return {
          key: o.key,
          resolver: buildCategoryMastersResult(masters),
          outputFormat: fmt.format && schema ? { ...fmt.format, schema } : fmt.format,
        };
      }),
    ).then((items) => {
      if (!cancelled) setLoaded(items);
    });
    return () => {
      cancelled = true;
    };
    // loadKey が観点の構成を表す (additional は毎回新しい配列になりうる)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey]);

  const postsById = useMemo(() => new Map(posts.map((p) => [p.id, p])), [posts]);
  return useMemo(
    () =>
      // 追加の観点が無い地区は空 (前の地区の読み込み結果を残さない)。
      (additional.length === 0 ? [] : loaded)
        .map((l): AnalysisPerspective | null => {
          const o = additional.find((x) => x.key === l.key);
          return o
            ? {
                key: l.key,
                label: o.label,
                resolver: l.resolver,
                outputFormat: l.outputFormat,
                postsById,
              }
            : null;
        })
        .filter((p): p is AnalysisPerspective => p !== null),
    [loaded, additional, postsById],
  );
}
